/**
 * harness/telemetry.js
 *
 * Telemetry sink. Records the raw inference API request/response log and
 * timestamped UI events without blocking the composer, inference path, or
 * interaction handlers.
 */

import { TelemetryEvent, isKnownEventType } from '../../shared/telemetry-events.js';

export const PRIVMETER_TELEMETRY_KEY = 'privmeter-telemetry';

const isDev = (() => {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
})();

const DEFAULT_ENDPOINT = '/api/telemetry';
const DEFAULT_BATCH_SIZE = 10;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_RETRY_BASE_MS = 200;
const DEFAULT_MAX_PERSISTED_BATCHES = 50;
const ENDPOINT_TIERS = Object.freeze({
  mirror: 'mirror',
  meter: 'meter',
  'message-findings': 'messageFindings',
  draft: 'draft',
  chat: 'chat',
});

function randomId() {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    // Fall through to a non-cryptographic correlation id.
  }
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultNow() {
  return new Date().toISOString();
}

function readPersisted(storage) {
  if (!storage?.getItem) return [];
  try {
    const raw = storage.getItem(PRIVMETER_TELEMETRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.length > 0;
}

function isValidPersistedEnvelope(batch) {
  // Match ingress validation and the store's non-null request payload column.
  // Defer malformed storage without starving valid later batches or rewriting it.
  if (!batch || typeof batch !== 'object' || Array.isArray(batch)) return false;
  if (!isNonEmptyString(batch.session_id) || !Array.isArray(batch.rows) || !batch.rows.length)
    return false;
  return batch.rows.every(
    (row) =>
      row !== null &&
      typeof row === 'object' &&
      !Array.isArray(row) &&
      isNonEmptyString(row.event_id) &&
      Number.isFinite(row.seq) &&
      isNonEmptyString(row.endpoint) &&
      Object.prototype.hasOwnProperty.call(row, 'request_payload') &&
      row.request_payload != null &&
      (row.latency_ms == null || Number.isFinite(row.latency_ms))
  );
}

function writePersisted(storage, batches, maxPersistedBatches) {
  if (!storage?.setItem) return;
  try {
    const trimmed = batches.slice(-maxPersistedBatches);
    if (trimmed.length === 0 && storage.removeItem) {
      storage.removeItem(PRIVMETER_TELEMETRY_KEY);
      return;
    }
    storage.setItem(PRIVMETER_TELEMETRY_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage can be full or unavailable. Telemetry must not surface.
  }
}

function appendPersisted(storage, batch, maxPersistedBatches) {
  if (!batch?.rows?.length) return;
  const existing = [...readPersisted(storage), batch];
  writePersisted(storage, existing, maxPersistedBatches);
}

function defaultStorage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function telemetryHeaders(authToken, participantToken) {
  const headers = { 'Content-Type': 'application/json' };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  // Lets the backend stamp server-authoritative participant attribution
  // instead of trusting the client-asserted participant_id/experiment/arm.
  if (participantToken) headers['x-participant-session'] = participantToken;
  return headers;
}

function defaultTransport(authToken) {
  return {
    async send(endpoint, envelope, participantToken) {
      // This is a browser-side application endpoint selected at build time.
      // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: telemetryHeaders(authToken, participantToken),
        keepalive: true,
        body: JSON.stringify(envelope),
      });
      if (!res.ok) throw new Error(`telemetry HTTP ${res.status}`);
    },
    beacon(endpoint, envelope, participantToken) {
      try {
        if (!globalThis.navigator?.sendBeacon) return false;
        const beaconEnvelope = {
          ...envelope,
          ...(authToken ? { telemetry_auth_token: authToken } : {}),
          ...(participantToken ? { participant_session_token: participantToken } : {}),
        };
        const blob = new Blob([JSON.stringify(beaconEnvelope)], { type: 'application/json' });
        return globalThis.navigator.sendBeacon(endpoint, blob);
      } catch {
        return false;
      }
    },
  };
}

/**
 * @param {object} [config]
 * @param {string} [config.endpoint]
 * @param {number} [config.batchSize]
 * @param {number} [config.flushIntervalMs]
 * @param {number} [config.maxRetries]
 * @param {string} [config.sessionId]
 * @param {string} [config.client]
 * @param {string|null} [config.provider]
 * @param {string|null} [config.model]
 * @param {string|null} [config.authToken]
 * @param {string} [config.contractVersion]
 * @param {() => string} [config.now]
 * @param {{send?:Function, beacon?:Function}} [config.transport]
 * @param {Storage} [config.storage]
 */
export function createTelemetry(config = {}) {
  const endpoint = config.endpoint ?? DEFAULT_ENDPOINT;
  const batchSize = config.batchSize ?? DEFAULT_BATCH_SIZE;
  const flushIntervalMs = config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  const maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
  const retryBaseMs = config.retryBaseMs ?? DEFAULT_RETRY_BASE_MS;
  const maxPersistedBatches = config.maxPersistedBatches ?? DEFAULT_MAX_PERSISTED_BATCHES;
  const configIsDev = Object.prototype.hasOwnProperty.call(config, 'isDev')
    ? Boolean(config.isDev)
    : isDev;
  const sessionId = config.sessionId ?? randomId();
  const authToken = config.authToken ?? null;
  const now = config.now ?? defaultNow;
  const transport = config.transport ?? defaultTransport(authToken);
  const storage = config.storage ?? defaultStorage();
  const windowTarget = config.windowTarget ?? (typeof window !== 'undefined' ? window : null);
  const documentRef = config.documentRef ?? (typeof document !== 'undefined' ? document : null);
  let context = {
    client: config.client ?? null,
    provider: config.provider ?? null,
    model: config.model ?? null,
    contractVersion: config.contractVersion ?? null,
    participantId: config.participantId ?? null,
    experiment: config.experiment ?? null,
    arm: config.arm ?? null,
    participantSessionToken: config.participantSessionToken ?? null,
  };

  let seq = 0;
  let buffer = [];
  let flushTimer = null;
  let disposed = false;
  let inFlight = Promise.resolve();
  let drainQueued = false;

  const valueForEndpoint = (value, endpointName) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const tier = ENDPOINT_TIERS[endpointName] ?? endpointName;
      return value[endpointName] ?? value[tier] ?? null;
    }
    return value ?? null;
  };

  const baseFields = (endpointName) => ({
    session_id: sessionId,
    client: context.client,
    provider: valueForEndpoint(context.provider, endpointName),
    model: valueForEndpoint(context.model, endpointName),
    contract_version: context.contractVersion,
    participant_id: context.participantId,
    experiment: context.experiment,
    arm: context.arm,
  });

  const makeEnvelope = (rows) => ({ session_id: sessionId, rows });

  const clearFlushTimer = () => {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  };

  const scheduleFlush = (delay = flushIntervalMs) => {
    if (disposed || flushTimer !== null) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, delay);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const sendEnvelope = async (envelope, participantToken) => {
    if (!envelope.rows?.length) return false;
    let lastErr = null;
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        await transport.send(endpoint, envelope, participantToken);
        return true;
      } catch (err) {
        lastErr = err;
        if (attempt < maxRetries) {
          await sleep(retryBaseMs * 2 ** attempt);
        }
      }
    }
    if (lastErr && config.onError) {
      try {
        config.onError(lastErr);
      } catch {
        /* observer bug ignored */
      }
    }
    return false;
  };

  const canReplay = (batch) => {
    // Never authenticate former or unknown-owner rows as the active participant.
    // Startup waits for participant context; researcher rows stay unauthenticated.
    const hasParticipant = Boolean(context.participantId && context.participantSessionToken);
    if (!hasParticipant && context.participantSessionToken) return false;
    if (!hasParticipant && (context.participantId || context.experiment || context.arm))
      return false;
    if (hasParticipant && (!context.experiment || !context.arm)) return false;
    return batch.rows.every(
      (row) =>
        (row?.participant_id ?? null) === context.participantId &&
        (row?.experiment ?? null) === context.experiment &&
        (row?.arm ?? null) === context.arm
    );
  };

  // Keep each batch durable until acknowledged. Replay never initiates another
  // replay, and stops at the first exhausted failure instead of cycling it.
  const replayPersisted = async () => {
    const persisted = readPersisted(storage).slice(0, maxPersistedBatches);
    for (const batch of persisted) {
      if (!isValidPersistedEnvelope(batch) || !canReplay(batch)) continue;
      if (!(await sendEnvelope(batch, context.participantSessionToken))) break;
      const remaining = readPersisted(storage).filter(
        (candidate) =>
          candidate?.session_id !== batch.session_id ||
          !Array.isArray(candidate?.rows) ||
          candidate?.rows?.length !== batch.rows.length ||
          candidate.rows.some((row, index) => row?.event_id !== batch.rows[index]?.event_id)
      );
      writePersisted(storage, remaining, maxPersistedBatches);
    }
  };

  const drainPersisted = () => {
    if (drainQueued || disposed) return;
    drainQueued = true;
    inFlight = inFlight.then(async () => {
      try {
        await replayPersisted();
      } finally {
        drainQueued = false;
      }
    });
  };

  function flush(options = {}) {
    clearFlushTimer();
    const rows = buffer;
    buffer = [];
    const envelope = makeEnvelope(rows);
    const participantToken = context.participantSessionToken;
    if (
      options.preferBeacon &&
      rows.length > 0 &&
      transport.beacon?.(endpoint, envelope, participantToken)
    ) {
      return inFlight;
    }
    if (rows.length === 0) return inFlight;
    inFlight = inFlight.then(async () => {
      if (await sendEnvelope(envelope, participantToken)) {
        // A successful ordinary fetch proves reachability even when the browser
        // stayed online throughout the backend failure.
        if (!disposed) await replayPersisted();
      } else {
        appendPersisted(storage, envelope, maxPersistedBatches);
      }
    });
    return inFlight;
  }

  const enqueue = (row) => {
    try {
      if (disposed) return;
      const endpointName = row?.endpoint ?? null;
      const stamped = {
        event_id: randomId(),
        seq: ++seq,
        timestamp: now(),
        ...baseFields(endpointName),
        ...row,
      };
      buffer.push(stamped);
      if (buffer.length >= batchSize) {
        clearFlushTimer();
        scheduleFlush(0);
      } else {
        scheduleFlush(flushIntervalMs);
      }
    } catch {
      // Never let telemetry failures surface into inference.
    }
  };

  const recordInference = (row) => {
    enqueue(row);
  };

  const recordEvent = (type, detail = {}, correlation = {}) => {
    if (typeof type !== 'string' || type.length === 0) {
      if (config.onError) {
        try {
          config.onError(new Error('Invalid telemetry event type'));
        } catch {
          /* observer bug ignored */
        }
      }
      return;
    }
    // Every call site is migrated onto the TelemetryEvent constants
    // (shared/telemetry-events.js) — an unrecognized type here means a
    // typo, a stale wire string, or a new event added without registering
    // it. In dev/test that's a mistake to fail loudly on before it ships.
    // In production, failing loudly would drop a participant's row
    // entirely; warn instead and still record it under its raw type so
    // the session isn't silently corrupted, and reconcile the vocabulary
    // later against the row's own name in the dashboard's Behavior panel.
    if (!isKnownEventType(type)) {
      const err = new Error(
        `[telemetry] unknown Stream B event type "${type}" — add it to shared/telemetry-events.js`
      );
      if (configIsDev) throw err;
      console.warn(err.message);
      if (config.onError) {
        try {
          config.onError(err);
        } catch {
          /* observer bug ignored */
        }
      }
    }
    enqueue({
      endpoint: `event.${type}`,
      conversation_id: correlation.conversationId ?? correlation.conversation_id ?? null,
      msg_id: correlation.msgId ?? correlation.msg_id ?? null,
      request_payload: detail ?? {},
      response_payload: null,
    });
    // message_sent and study_complete both precede a likely navigation away
    // (send-then-close-tab; finish-then-follow-the-Qualtrics-link), so both
    // force an immediate beacon flush rather than waiting for the batch timer.
    if (type === TelemetryEvent.MESSAGE_SENT || type === TelemetryEvent.STUDY_COMPLETE)
      void flush({ preferBeacon: true });
  };

  const forceFlush = () => {
    if (buffer.length > 0) void flush({ preferBeacon: true });
  };

  const onVisibilityChange = () => {
    if (documentRef?.visibilityState === 'hidden') forceFlush();
  };

  const onOnline = () => {
    drainPersisted();
    if (buffer.length > 0) void flush();
  };

  drainPersisted();
  documentRef?.addEventListener?.('visibilitychange', onVisibilityChange);
  windowTarget?.addEventListener?.('pagehide', forceFlush);
  windowTarget?.addEventListener?.('online', onOnline);

  const dispose = () => {
    disposed = true;
    clearFlushTimer();
    documentRef?.removeEventListener?.('visibilitychange', onVisibilityChange);
    windowTarget?.removeEventListener?.('pagehide', forceFlush);
    windowTarget?.removeEventListener?.('online', onOnline);
  };

  return {
    sessionId,
    recordInference,
    recordEvent,
    flush,
    dispose,
    setContext(next = {}) {
      // Flush rows under their original session before replacing attribution.
      if (
        buffer.length > 0 &&
        ['participantId', 'experiment', 'arm', 'participantSessionToken'].some(
          (key) => Object.prototype.hasOwnProperty.call(next, key) && next[key] !== context[key]
        )
      ) {
        void flush();
      }
      const updates = Object.fromEntries(
        Object.keys(context)
          .filter((key) => Object.prototype.hasOwnProperty.call(next, key))
          .map((key) => [key, next[key]])
      );
      context = { ...context, ...updates };
    },
  };
}

export function createNoopTelemetry() {
  return {
    sessionId: null,
    recordInference() {},
    recordEvent() {},
    flush: async () => {},
    dispose() {},
    setContext() {},
  };
}
